import moment from "moment";
import TransactionCardSkeleton from "@/components/skeletons/transaction-card-skeleton";
import { useSheetStore } from "@/zustand/sheetStore";
import Pagination from "@/components/pagination";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { getCardTransactionDisplayAmount } from "@/lib/utils";

const tabToTypeMap: Record<string, string> = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  transfer: "Transfer",
  "card-topup": "Card Topup",
  "card-withdrawal": "Card Withdrawal",
  "card-creation": "Card Creation",
  "referral-reward": "Referral Reward",
};

const typeMap = {
  TransferIn: "Card Deposit",
  TransferOut: "Card Withdrawal",
  Fee_Consumption: "Fee",
  auth: "Authorization",
  Void: "Void",
};

const TransactionCard = ({ transaction }: { transaction: Transaction }) => {
  const formatAmount = useFormatAmountWithCurrency();
  const sign = transaction.type === "deposit" ? "+" : "-";
  const { openSheet } = useSheetStore();
  return (
    <div
      onClick={() =>
        openSheet("transaction-details", null, {
          transactionData: transaction,
          type: "wallet",
        })
      }
      className="text-dark-text-50 border-dark-stroke-5 bg-dark-card-3 space-y-1 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between text-sm">
        <span>{transaction.currency || "USD"}</span>
        <div>
          {sign}
          {formatAmount(transaction.amount)}
        </div>
      </div>
      <div className="text-dark-text-50 flex items-center justify-between text-[10px]">
        <span>{moment(transaction.createdAt).format("DD-MM-YY hh:mm A")}</span>
        <span>{tabToTypeMap[transaction?.type]}</span>
      </div>
      <div className="text-dark-text-50 flex items-center justify-between text-[10px]">
        <span>Id: {transaction?.id}</span>
        <StatusBadge status={transaction.status} />
      </div>
    </div>
  );
};

const CardTransactionCard = ({
  transaction,
}: {
  transaction: CardTransaction;
}) => {
  const { openSheet } = useSheetStore();
  const { amount: displayAmount, currency: displayCurrency } =
    getCardTransactionDisplayAmount(transaction);
  const sign =
    displayAmount === 0
      ? ""
      : transaction.type === "TransferIn" || transaction.type === "Void"
        ? "+"
        : transaction.type === "TransferOut" || transaction.type === "auth"
          ? "-"
          : "";

  return (
    <div
      onClick={() =>
        openSheet("transaction-details", null, {
          transactionData: transaction,
          type: "card",
        })
      }
      className="border-dark-stroke-5 bg-dark-card-3 text-dark-text-50 space-y-1 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between text-sm">
        <span>
          {transaction.merchantName ||
            typeMap[transaction.type] ||
            transaction.type}
        </span>

        <div>
          {sign}
          {displayAmount?.toFixed(2)} {displayCurrency}
        </div>
      </div>
      <div className="text-dark-text-50 flex items-center justify-between text-[10px]">
        <span>{moment(transaction.transactionTime).format("DD-MM-YY hh:mm A")}</span>
        <StatusBadge status={transaction.status} />
      </div>
    </div>
  );
};

const TransactionList = ({
  transactions,
  isLoading = false,
  type = "wallets",
  handlePageChange,
  totalPages,
  currentPage,
}: {
  transactions: Transaction[] | CardTransaction[];
  isLoading?: boolean;
  type?: "wallets" | "cards";
  handlePageChange?: (page: number) => void;
  totalPages?: number;
  currentPage?: number;
}) => {
  if (isLoading)
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <TransactionCardSkeleton key={index} />
        ))}
      </div>
    );

  if (transactions.length === 0)
    return (
      <div>
        <div className="py-20">
          <p className="text-dark-text-50 text-center">No transactions yet</p>
        </div>
      </div>
    );
  return (
    <div>
      <div className="space-y-4">
        {transactions.map((transaction) => {
          if (type === "wallets")
            return (
              <TransactionCard
                transaction={transaction as Transaction}
                key={transaction?.id}
              />
            );

          if (type === "cards")
            return (
              <CardTransactionCard
                transaction={transaction as CardTransaction}
                key={transaction?.id}
              />
            );
        })}
      </div>

      {type === "wallets" && totalPages && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages || 1}
          onPageChange={handlePageChange as (page: number) => void}
        />
      )}

      {type === "cards" && totalPages && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalPages / 10) - 1 || 1}
          onPageChange={handlePageChange as (page: number) => void}
        />
      )}
    </div>
  );
};

type Status =
  | "closed"
  | "pending"
  | "waiting"
  | "completed"
  | "failed"
  | "rejected"
  | "fail";

interface StatusBadgeProps {
  status: string;
}

const statusStyles: Record<
  Status,
  { text: string; bg: string; textColor: string }
> = {
  closed: {
    text: "Closed",
    bg: "bg-dark-warning-300",
    textColor: "text-dark-warning-300",
  },
  pending: {
    text: "Pending",
    bg: "bg-dark-warning-300",
    textColor: "text-dark-warning-300",
  },
  waiting: {
    text: "Waiting",
    bg: "bg-dark-warning-300",
    textColor: "text-dark-warning-300",
  },
  completed: {
    text: "Completed",
    bg: "bg-dark-success-300",
    textColor: "text-dark-success-300",
  },
  failed: {
    text: "Failed",
    bg: "bg-dark-error-300",
    textColor: "text-dark-error-300",
  },
  fail: {
    text: "Failed",
    bg: "bg-dark-error-300",
    textColor: "text-dark-error-300",
  },
  rejected: {
    text: "Rejected",
    bg: "bg-dark-error-300",
    textColor: "text-dark-error-300",
  },
};

/* The `Transaction["status"]` / `CardTransaction["status"]` unions are .d.ts
   assertions over `response.data`, not runtime-validated, so a status outside
   the map above does reach here — an unlisted provider value, or a row with no
   status at all. Destructuring the lookup directly threw a TypeError and blanked
   the screen; the fallback shows the raw value in neutral grey instead, so an
   unmapped status stays visible rather than being silently swallowed.

   Only this list crashed: the sibling badge in `_misc/columns.tsx` reads every
   field through `config?.`, and that one serves the desktop table while this one
   serves the `lg:hidden` mobile cards. */
const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const { text, bg, textColor } = statusStyles[
    status?.toLowerCase() as keyof typeof statusStyles
  ] ?? {
    text: status ?? "Unknown",
    bg: "bg-dark-text-300",
    textColor: "text-dark-text-300",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full py-1 text-[10px] ${textColor}`}
    >
      <span className={`mr-1 size-1 rounded-full ${bg}`} />
      {text}
    </span>
  );
};

export default TransactionList;
